'use client'

import { FC, useEffect, useState } from 'react'

interface NextVideoPreloaderProps {
  /** 次に再生される動画の署名付きURL。最後の動画など次が無い場合は null。 */
  videoUrl: string | null
}

/**
 * 現在の動画の読み込みを優先するため、少し待ってから次動画の「出だし」を
 * 画面外の hidden video で低優先に先読みする。短い動画なら次への切替がほぼ瞬時になる。
 * まるごと強制ダウンロードはせず、ブラウザが出だしから順に取得する。
 */
const PREFETCH_DELAY_MS = 2000

const NextVideoPreloader: FC<NextVideoPreloaderProps> = ({ videoUrl }) => {
  const [shouldPreload, setShouldPreload] = useState(false)

  // 動画が切り替わるたびにリセットし、現在の動画の読み込みを邪魔しないよう遅延後に先読み開始
  useEffect(() => {
    setShouldPreload(false)
    if (!videoUrl) return
    const timer = setTimeout(() => setShouldPreload(true), PREFETCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [videoUrl])

  if (!shouldPreload || !videoUrl) return null

  // データセーバー(Save-Data)有効時は、モバイル通信量を尊重して先読みしない
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  if (connection?.saveData) return null

  return (
    <>
      <style>{`.moovy-next-preloader{position:absolute;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;}`}</style>
      <video
        className="moovy-next-preloader"
        src={videoUrl}
        preload="auto"
        muted
        playsInline
        tabIndex={-1}
        aria-hidden="true"
      />
    </>
  )
}

export default NextVideoPreloader
